/**
 * プロジェクト内アンビエント背景の色導出。
 *
 * バナー色マスタ（src/constant/color.js の colorSetting）から代表色を抽出し、
 * - `--soremo-background` 差し替え用の「白にごく薄く溶かした不透明色」
 *   （有彩色は純白 #ffffff ベースでグレーかぶりを避け、無彩色バナーのみ
 *   素の --soremo-background と同じ #fcfcfc ベースを保つ）
 * - ゆっくり漂うグラデーション blob 用の「低アルファ色 x3」
 * を返す。独自の色表は持たず、バナー定義（colorSetting）とドリフトしない。
 *
 * `color-mix()` は使わず JS で解決する。未対応ブラウザで
 * `background-color` が無効値（透明）になる事故を避けるため。
 */
import { colorSetting } from "../constant/color";

// ---- 調整用定数（濃度・見え方はここだけ触れば足りる） ----

/** --soremo-background に混ぜるアクセントの比率（白基調を崩さない範囲） */
const TINT_MIX = 0.055;

/** blob 3 枚（先頭 / 中央 / 末尾の代表色）のアルファ */
const ACCENT_ALPHAS = [0.15, 0.12, 0.1] as const;

/** 淡色バナーの彩度正規化: チャンネル差（max - min）の目標値 */
const CHROMA_TARGET = 56;

/** 彩度正規化の倍率上限（元の色相を壊さないためのガード） */
const CHROMA_MAX_SCALE = 4;

/** これより明るい（平均チャンネル値が大きい）色だけ彩度正規化の対象にする */
const PALE_LUMA_THRESHOLD = 180;

/** ほぼ無彩色とみなすチャンネル差 */
const ACHROMATIC_SPREAD = 6;

/** 無彩色バナーでも blob がかすかに視認できるよう平均チャンネル値をここまで下げる */
const NEUTRAL_LUMA_CAP = 210;

/** 無彩色バナー用の tint ベース（--soremo-background の素の値 #fcfcfc） */
const BASE_RGB: Rgb = { r: 252, g: 252, b: 252 };

/** 有彩色バナー用の tint ベース。純白から混ぜてグレーかぶり（暗さ）を出さない */
const WHITE_RGB: Rgb = { r: 255, g: 255, b: 255 };

/** BE の get_banner_color_style と同じフォールバック先 */
const DEFAULT_BANNER_COLOR_KEY = "1";

export interface Rgb {
  r: number;
  g: number;
  b: number;
}

export interface ProjectAmbient {
  /** `--soremo-background` 差し替え用の不透明色（rgb() 文字列） */
  background: string;
  /** アンビエント blob 用の低アルファ色（バナーの先頭 / 中央 / 末尾に対応） */
  accents: [string, string, string];
}

const COLOR_TOKEN_RE = /#[0-9a-fA-F]{3,8}|rgba?\([^)]*\)/g;

const clamp255 = (value: number): number =>
  Math.min(255, Math.max(0, Math.round(value)));

/**
 * 色トークン 1 つを RGB に解決する。半透明の stop（banner-color16 の
 * rgba(...)）は「白の上に置いた見た目」として不透明化する。
 */
const parseColorToken = (token: string): Rgb | null => {
  if (token.startsWith("#")) {
    const hex = token.slice(1);
    if (hex.length === 3) {
      return {
        r: parseInt(hex[0] + hex[0], 16),
        g: parseInt(hex[1] + hex[1], 16),
        b: parseInt(hex[2] + hex[2], 16),
      };
    }
    if (hex.length >= 6) {
      return {
        r: parseInt(hex.slice(0, 2), 16),
        g: parseInt(hex.slice(2, 4), 16),
        b: parseInt(hex.slice(4, 6), 16),
      };
    }
    return null;
  }
  const nums = token.match(/[\d.]+/g)?.map(Number) ?? [];
  if (nums.length < 3) return null;
  const [r, g, b, a = 1] = nums;
  return {
    r: clamp255(r * a + 255 * (1 - a)),
    g: clamp255(g * a + 255 * (1 - a)),
    b: clamp255(b * a + 255 * (1 - a)),
  };
};

/**
 * background 定義（単色 or linear-gradient）から色 stop を順に抽出する。
 * バナー背景から代表色を導くユーティリティ（projectBannerColor 等）と共用する。
 */
export const extractStops = (background: string): Rgb[] =>
  (background.match(COLOR_TOKEN_RE) ?? [])
    .map(parseColorToken)
    .filter((c): c is Rgb => c !== null);

/**
 * 淡色バナーの救済。白に寄りすぎた代表色は、そのままでは低アルファで
 * 完全に消えてしまうため、色みを保ったまま彩度を一定水準まで引き上げる。
 * - 無彩色（グレー系）は彩度を持たないので、明度だけをわずかに下げる
 * - 濃色はそのまま（k は 1 未満にしない = 彩度を落とす方向には使わない）
 */
const normalizeAccent = (color: Rgb): Rgb => {
  const mean = (color.r + color.g + color.b) / 3;
  const spread =
    Math.max(color.r, color.g, color.b) - Math.min(color.r, color.g, color.b);

  if (spread <= ACHROMATIC_SPREAD) {
    if (mean <= NEUTRAL_LUMA_CAP) return color;
    const shift = mean - NEUTRAL_LUMA_CAP;
    return {
      r: clamp255(color.r - shift),
      g: clamp255(color.g - shift),
      b: clamp255(color.b - shift),
    };
  }

  if (mean < PALE_LUMA_THRESHOLD || spread >= CHROMA_TARGET) return color;

  const k = Math.min(CHROMA_TARGET / spread, CHROMA_MAX_SCALE);
  return {
    r: clamp255(mean + (color.r - mean) * k),
    g: clamp255(mean + (color.g - mean) * k),
    b: clamp255(mean + (color.b - mean) * k),
  };
};

const mix = (from: Rgb, to: Rgb, amount: number): Rgb => ({
  r: clamp255(from.r + (to.r - from.r) * amount),
  g: clamp255(from.g + (to.g - from.g) * amount),
  b: clamp255(from.b + (to.b - from.b) * amount),
});

const toRgb = (c: Rgb): string => `rgb(${c.r}, ${c.g}, ${c.b})`;

const toRgba = (c: Rgb, alpha: number): string =>
  `rgba(${c.r}, ${c.g}, ${c.b}, ${alpha})`;

const cache = new Map<string, ProjectAmbient>();

/**
 * banner_color（"1"〜"18"）からプロジェクト内アンビエント背景の色一式を得る。
 * 未知値・空値は "1" にフォールバックする（BE と同じ挙動）。
 */
export const getProjectAmbient = (
  bannerColor?: string | null,
): ProjectAmbient => {
  const trimmed = bannerColor == null ? "" : String(bannerColor).trim();
  const key = trimmed === "" ? DEFAULT_BANNER_COLOR_KEY : trimmed;

  const cached = cache.get(key);
  if (cached) return cached;

  const setting =
    colorSetting.find((c) => String(c.id) === key) ??
    colorSetting.find((c) => String(c.id) === DEFAULT_BANNER_COLOR_KEY);
  const stops = setting ? extractStops(setting.css_code.background) : [];
  // colorSetting が正しい限り到達しない保険（banner-color1 と同じダークグレー）
  if (stops.length === 0) stops.push({ r: 83, g: 86, b: 90 });

  // 先頭 / 中央 / 末尾の 3 stop を代表色に採用（単色バナーは同色 x3）
  const representatives = [
    stops[0],
    stops[Math.floor((stops.length - 1) / 2)],
    stops[stops.length - 1],
  ].map(normalizeAccent);

  // tint のベース: 有彩色バナーは純白（#fcfcfc 由来のグレーを持ち込まない）、
  // 無彩色バナーは素の --soremo-background と揃えて #fcfcfc のまま
  const tintSource = representatives[1];
  const tintSpread =
    Math.max(tintSource.r, tintSource.g, tintSource.b) -
    Math.min(tintSource.r, tintSource.g, tintSource.b);
  const tintBase = tintSpread <= ACHROMATIC_SPREAD ? BASE_RGB : WHITE_RGB;

  const ambient: ProjectAmbient = {
    background: toRgb(mix(tintBase, tintSource, TINT_MIX)),
    accents: [
      toRgba(representatives[0], ACCENT_ALPHAS[0]),
      toRgba(representatives[1], ACCENT_ALPHAS[1]),
      toRgba(representatives[2], ACCENT_ALPHAS[2]),
    ],
  };

  cache.set(key, ambient);
  return ambient;
};
